import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { money, when } from "@/lib/rides";
import { PageHead } from "@/pages/admin/AdminLayout";
import { StatusBadge } from "@/components/RideStatus";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export const usePatchUser = () => {
  const qc = useQueryClient();
  return async (id, body, msg = "User updated") => {
    try {
      await api.patch(`/admin/users/${id}`, body);
      toast.success(msg);
      ["adminUsers", "adminProfile", "adminDrivers", "adminDashboard"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
};

function RemoveUpcoming({ id, onDone }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const { data: s, refetch } = useQuery({ queryKey: ["upcomingSummary", id], queryFn: () => api.get(`/admin/users/${id}/upcoming-summary`).then((r) => r.data), enabled: open });
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/users/${id}/cancel-upcoming`, { expected_count: s.count });
      toast.success(`Cancelled ${data.cancelled} upcoming rides`);
      ["adminProfile", "adminDashboard", "adminOverview"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
      setOpen(false);
      onDone();
    } catch (e) {
      toast.error(formatApiError(e));
      refetch();
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <Button size="sm" variant="destructive" onClick={() => setOpen(true)} data-testid="remove-upcoming-button">Remove All Upcoming Rides</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card" data-testid="remove-upcoming-dialog">
          <DialogHeader><DialogTitle>Remove all upcoming rides?</DialogTitle></DialogHeader>
          {s && (
            <div className="space-y-3 text-sm">
              <p data-testid="remove-upcoming-rider"><b>{s.name}</b> · {s.email}{s.phone ? ` · ${s.phone}` : ""}</p>
              <p data-testid="remove-upcoming-count"><b>{s.count}</b> upcoming ride{s.count === 1 ? "" : "s"} will be cancelled{s.recurring_schedules ? `, including future dates of ${s.recurring_schedules} recurring schedule${s.recurring_schedules > 1 ? "s" : ""}` : ""}. {s.drivers_affected} driver{s.drivers_affected === 1 ? "" : "s"} will be notified.</p>
              <p className="text-xs text-muted-foreground">Completed rides, ride history, messages and the rider's account are kept. Held Ride Credit is released.</p>
              <Button variant="destructive" disabled={busy || !s.count} onClick={run} data-testid="remove-upcoming-confirm">Cancel {s.count} ride{s.count === 1 ? "" : "s"}</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function Profile({ id, onClose }) {
  const patch = usePatchUser();
  const { data, refetch } = useQuery({ queryKey: ["adminProfile", id], queryFn: () => api.get(`/admin/users/${id}`).then((r) => r.data) });
  const [notes, setNotes] = useState(null);
  const u = data?.user;
  const List = ({ title, rides }) => (
    <div className="space-y-1"><p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{title} ({rides.length})</p>
      {rides.slice(0, 8).map((r) => <div key={r.id} className="flex justify-between gap-2 text-xs"><span className="truncate">{when(r.scheduled_at)} · {r.pickup.name} → {r.dropoff.name}</span><span className="flex shrink-0 gap-2"><StatusBadge status={r.status} />{money(r.fare)}</span></div>)}
    </div>
  );
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto bg-card" data-testid="user-profile-dialog">
        <DialogHeader><DialogTitle>{u?.name}</DialogTitle></DialogHeader>
        {u && (
          <div className="space-y-4 text-sm">
            <p className="text-muted-foreground">{u.email}{u.phone ? ` · ${u.phone}` : ""} · {u.account_type}{u.admin_level ? ` · ${u.admin_level}` : ""}{!u.claimed ? " · not yet claimed" : ""}</p>
            <p className="text-xs">Unpaid completed rides: <span className="font-semibold">{data.unpaid_count}</span></p>
            <div className="flex flex-wrap items-center gap-3 rounded-lg border p-3 text-xs" data-testid="profile-billing">
              <span>Ride Credit: <span className="font-semibold" data-testid="profile-credit">${(u.credit_balance || 0).toFixed(2)}</span>{u.credit_reserved ? ` (${u.credit_reserved.toFixed(2)} reserved)` : ""}</span>
              <label className="flex items-center gap-2">Post-Payment:
                <select value={u.post_payment_status || ""} data-testid="profile-post-payment-select" className="h-8 rounded-md border bg-background px-2"
                  onChange={async (e) => { try { await api.post(`/admin/post-payment/riders/${id}`, { status: e.target.value }); toast.success("Post-payment updated"); refetch(); } catch (err) { toast.error(formatApiError(err)); } }}>
                  <option value="" disabled>Not applied</option><option value="pending">Pending Review</option><option value="approved">Approved</option>
                  <option value="declined">Declined</option><option value="suspended">Suspended</option>
                </select>
              </label>
            </div>
            <List title="Upcoming" rides={data.upcoming} />
            <RemoveUpcoming id={id} onDone={refetch} />
            <List title="Ride history" rides={data.history} />
            <List title="Cancelled / declined" rides={data.cancelled} />
            <div className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Admin notes</p>
              <Textarea value={notes ?? u.admin_notes ?? ""} onChange={(e) => setNotes(e.target.value)} data-testid="admin-notes-input" />
              <Button size="sm" onClick={() => patch(id, { admin_notes: notes ?? "" }, "Notes saved")} data-testid="admin-notes-save">Save notes</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function AddUser({ onClose }) {
  const qc = useQueryClient();
  const [f, setF] = useState({ name: "", email: "", phone: "" });
  const save = async () => {
    try {
      const { data } = await api.post("/admin/users", { ...f, phone: f.phone || null });
      toast.success(data.existing ? "Matching account already exists — no duplicate created" : "Customer created. They can claim it by signing up with this email.");
      qc.invalidateQueries({ queryKey: ["adminUsers"] });
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-card" data-testid="add-user-dialog">
        <DialogHeader><DialogTitle>Add customer</DialogTitle></DialogHeader>
        {["name", "email", "phone"].map((k) => <Input key={k} placeholder={k} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`add-user-${k}`} />)}
        <Button onClick={save} disabled={!f.name || !f.email} data-testid="add-user-save">Create</Button>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminPeople() {
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState("");
  const [profile, setProfile] = useState(null);
  const patch = usePatchUser();
  const { data } = useQuery({ queryKey: ["adminUsers", q], queryFn: () => api.get("/admin/users", { params: { q: q || undefined } }).then((r) => r.data) });
  return (
    <div className="fade-up" data-testid="admin-users">
      <PageHead title="Riders" sub="Riders, drivers and admins. One account per person; open a profile to manage their rides.">
        <Input placeholder="Search" value={q} onChange={(e) => setQ(e.target.value)} className="h-9 w-56" data-testid="admin-users-search" />
        <Button onClick={() => setParams({ add: "1" })} data-testid="open-add-user">+ Add user</Button>
      </PageHead>
      <div className="divide-y rounded-xl border bg-card">
        {data?.map((u) => (
          <div key={u.id} className="flex flex-wrap items-center justify-between gap-3 p-4" data-testid="admin-user-row">
            <button type="button" onClick={() => setProfile(u.id)} className="text-left" data-testid={`open-profile-${u.id}`}>
              <p className="font-semibold">{u.name} {u.is_owner && <span className="ml-1 rounded-full bg-primary px-2 py-0.5 text-[10px] text-primary-foreground">OWNER</span>}</p>
              <p className="text-xs text-muted-foreground">{u.email}{u.phone ? ` · ${u.phone}` : ""} · {u.account_type}{u.driver_status ? ` · driver ${u.driver_status}` : ""}</p>
            </button>
            {!u.is_owner && (
              <div className="flex items-center gap-3 text-xs">
                <select value={u.admin_role || "none"} onChange={(e) => patch(u.id, { admin_role: e.target.value }, "Role updated")} className="h-8 rounded-md border bg-background px-2" data-testid={`user-admin-role-${u.id}`}>
                  <option value="none">No admin</option><option value="staff_admin">Staff admin</option><option value="admin">Admin</option>
                </select>
                <label className="flex items-center gap-1">Active <Switch checked={u.is_active} onCheckedChange={(v) => patch(u.id, { is_active: v })} data-testid={`user-active-switch-${u.id}`} /></label>
              </div>
            )}
          </div>
        ))}
      </div>
      {profile && <Profile id={profile} onClose={() => setProfile(null)} />}
      {params.get("add") && <AddUser onClose={() => setParams({})} />}
    </div>
  );
}
